import React, { useState } from 'react'
import { Divider, Checkbox, Switch } from '@blueprintjs/core'
import {
  useLocationBookableDesks,
  ILocationBookableDeskHook,
} from './hooks/useLocationBookableDesks'
import { ILocation, ILocationBookableDesk } from '@api'
import api from '@api'
import { useReload } from '@hooks/reloadContext'
import LocationSetting from './LocationSetting'
import { capitalizeStr } from '@utils'
import { isEqual, union } from 'lodash'
import { Confirmation, Toaster } from '@ui'

export default function LocationBookableDesk({
  bookableDesks,
  requiredDesks,
  bookingLocationEnabled,
  toggleLocation,
}: {
  bookableDesks: string[]
  requiredDesks: string[]
  bookingLocationEnabled: boolean
  toggleLocation: () => void
}) {
  const hook = useLocationBookableDesks()
  const reload = useReload()
  const {
    list: disabledLocations,
    dups: disabledLocationIds,
    groups,
  } = hook.bookableDesks.reduce(
    (a: any, b, i) => {
      if (b.locations) {
        const desks = b.bookableSpaceTypes.join('-')
        a = {
          ...a,
          list: [...a.list, ...b.locations],
          dups: { ...a.dups, ...b.locations.map(({ id }) => id) },
          groups: { ...a.groups, [desks]: i },
        }
      }
      return a
    },
    { list: [], dups: [], groups: {} }
  )

  return (
    <>
      <div className="my-4 mt-6">
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <div className="font-medium mb-3">Book desk types by location</div>

            <div className="text-sm text-grey-700">
              Specify desk types to be booked within a location group.
            </div>
          </div>
          <div>
            <Switch
              large
              innerLabelChecked="ON"
              innerLabel="OFF"
              checked={bookingLocationEnabled}
              onChange={toggleLocation}
            />
          </div>
        </div>

        {bookingLocationEnabled && (
          <LocationSetting
            locationList={hook.bookableDesks}
            disabledLocations={disabledLocations}
            createButtonText="Add Location"
            deleteButtonText="Delete Location"
            onAddClose={() => {
              reload.locationBookableDesks()
            }}
            editable={true}
            deleteLocation={({ locations }: { locations: ILocation[] }) => {
              return api.company.locationBookableDesks
                .remove({ locationIds: locations.map(({ id }) => id) })
                .then(() => {
                  Toaster.success({ message: 'Location successfully deleted' })
                  reload.locationBookableDesks()
                })
            }}
            deleteConfirmContent={() => (
              <div style={{ maxWidth: 400 }}>
                <div className="mb-2">Are you sure you want to delete this Location Group?</div>
                <div className="text-sm whitespace-pre-wrap text-grey-700">
                  Employees in group locations won’t be able to book these desk types
                </div>
              </div>
            )}
            saveConfirmContent={({ bookableDesks = [], index, isCreate }) => {
              let prevDesks: string[] = []
              if (!isCreate) {
                prevDesks = hook.bookableDesks[index]?.bookableSpaceTypes
                  .filter((k: string) => !bookableDesks.includes(k))
                  .map((e: string) => e.replace('_', ' '))
              }
              return groups[bookableDesks.join('-')] !== undefined &&
                groups[bookableDesks.join('-')] !== index ? (
                <div style={{ maxWidth: 400 }}>
                  <div className="mb-2">Are you sure you want to save this Location Group?</div>
                  <div className="text-sm whitespace-pre-wrap text-grey-700">
                    {`This will be merged with a location group with the same selected desk${
                      bookableDesks.length > 1 ? 's' : ''
                    }`}
                    {prevDesks.length > 0 &&
                      `\nAll ${prevDesks.join(', ')} bookings will be canceled`}
                  </div>
                </div>
              ) : !isCreate && prevDesks.length > 0 ? (
                <div style={{ maxWidth: 400 }}>
                  <div className="mb-2">Are you sure you want to save this Location Group?</div>
                  <div className="text-sm whitespace-pre-wrap text-grey-700">
                    {`All ${prevDesks.join(', ')} bookings will be canceled`}
                  </div>
                </div>
              ) : null
            }}
            content={(data) => {
              return BookableDesk({
                ...data,
                requiredDesks,
                bookableDesks,
                disabledBookableDeskIds: disabledLocationIds,
                hook,
              })
            }}
            createContent={(locs: ILocation[], disabledLocations: ILocation[]) => {
              return LocationBookableDeskCreate({
                locs,
                requiredDesks,
                bookableDesks,

                disabledLocations,
                hook,
              })
            }}
          />
        )}
      </div>
    </>
  )
}

function BookableDesk({
  id,
  bookableSpaceTypes: bookableSpaceDesks = [],
  bookableDesks,
  requiredDesks,
  locations,
  isOpen,
  isEditing,
}: ILocationBookableDesk & {
  hook: ILocationBookableDeskHook
  id: string
  bookableSpaceDesks: string[]
  bookableDesks: string[]
  requiredDesks: string[]
  isOpen: boolean
  isEditing: boolean
}) {
  const [bookableSpaceTypes, setBookableSpaceTypes] = useState<string[]>(
    union(bookableSpaceDesks, requiredDesks)
  )

  const reload = useReload()

  const element = bookableSpaceTypes
    ?.map((e) => e.split('_').map(capitalizeStr).join(' '))
    .join(', ')

  return {
    locationTitle: 'Location Group',
    cancelEdit: () => {},
    saveDisabled: false,
    saveParams: { bookableDesks: bookableSpaceTypes },
    saveLocation: (locs: ILocation[]) => {
      const locIds = locs.map(({ id }) => id)
      const pLocationIds = locations.map(({ id }) => id)
      const deleteLocationIds = locations.filter((l) => !locIds.includes(l.id)).map(({ id }) => id)
      const addLocationIds = locs.filter((l) => !pLocationIds.includes(l.id)).map(({ id }) => id)
      if (
        !isEqual(locations, locs) &&
        (deleteLocationIds.length > 0 || addLocationIds.length > 0)
      ) {
        const deletePromise =
          deleteLocationIds.length > 0
            ? api.company.locationBookableDesks.remove({ locationIds: deleteLocationIds })
            : Promise.resolve()

        const addPromise =
          addLocationIds.length > 0
            ? api.company.locationBookableDesks.add({
                locationIds: addLocationIds,
                bookableSpaceTypes,
              })
            : Promise.resolve()

        return Promise.all([deletePromise, addPromise]).then(() => {
          api.company.locationBookableDesks
            .update({
              locationIds: locIds,
              bookableSpaceTypes,
              id,
            })
            .then(() => {
              Toaster.success({ message: 'Location successfully saved' })
            })
        })
      } else {
        return api.company.locationBookableDesks
          .update({
            locationIds: locIds,
            bookableSpaceTypes,
            id,
          })
          .then(() => {
            Toaster.success({ message: 'Location successfully saved' })
            reload.locationBookableDesks()
          })
      }
    },
    element: (
      <div className="my-4" key={id}>
        <div className="rounded pl-5 py-2 ">
          {!isEditing ? (
            <div>{element}</div>
          ) : (
            <BookableDeskEdit
              bookableSpaceTypes={bookableSpaceTypes}
              bookableDesks={bookableDesks}
              requiredDesks={requiredDesks}
              onChange={setBookableSpaceTypes}
            />
          )}
        </div>
        <Divider />
      </div>
    ),
  }
}

function LocationBookableDeskCreate({
  locs,
  requiredDesks,
  bookableDesks,
  disabledLocations,
}: {
  locs: ILocation[]
  bookableDesks: string[]
  requiredDesks: string[]
  disabledLocations: ILocation[]
  hook: ILocationBookableDeskHook
}) {
  const [bookableSpaceTypes, setBookableSpaceTypes] = useState<string[]>([...requiredDesks])
  const reload = useReload()

  return {
    saveParams: { bookableDesks: bookableSpaceTypes },
    newLocationText: 'New Location Group',
    saveDisabled: locs.length < 1 || bookableSpaceTypes.length < 1,
    addLocation: (locs: ILocation[]) => {
      return api.company.locationBookableDesks
        .add({
          bookableSpaceTypes,
          locationIds: locs.map(({ id }) => id),
        })
        .then(() => {
          Toaster.success({ message: 'Location successfully saved' })
          reload.locationBookableDesks()
        })
    },
    disabledLocationIds: [...disabledLocations.map(({ id }) => id)],
    element: (
      <div className="my-3">
        <div className="my-4">
          <BookableDeskEdit
            bookableSpaceTypes={bookableSpaceTypes}
            bookableDesks={bookableDesks}
            requiredDesks={requiredDesks}
            onChange={setBookableSpaceTypes}
          />
        </div>
      </div>
    ),
  }
}

export const BookableDeskEdit = ({
  bookableSpaceTypes,
  bookableDesks,
  requiredDesks,
  onChange,
  hasConfirmBox,
}: {
  bookableSpaceTypes: string[]
  bookableDesks: string[]
  requiredDesks: string[]
  onChange: (params: string[]) => void
  hasConfirmBox?: boolean
}) => {
  const [checkboxValue, setCheckboxValue] = useState<{ id?: string; value?: boolean }>({})
  const [isOpen, setIsOpen] = useState(false)
  const saveContent = (desk: string) => {
    return (
      <div style={{ maxWidth: 400 }}>
        <div className="mb-2">{`Are you sure you want to remove the bookable desk${
          desk === 'non hoteling desk' ? 's' : ''
        }?`}</div>
        <div className="text-sm whitespace-pre-wrap text-grey-700">
          {`All ${desk.replace('_', ' ')} bookings will be canceled`}
        </div>
      </div>
    )
  }
  return (
    <div>
      {hasConfirmBox ? (
        <Confirmation
          noIcon
          text={saveContent('non hoteling desk')}
          cancelable
          isOpen={isOpen && checkboxValue.id === 'all'}
          onConfirm={() => {
            onChange((checkboxValue?.value ? [...bookableDesks] : [...requiredDesks]).sort())
            setIsOpen(false)
          }}
          onCancel={() => setIsOpen(false)}
          onClosing={() => {
            setCheckboxValue({})
          }}
        >
          <Checkbox
            checked={(bookableSpaceTypes || []).length === bookableDesks.length}
            style={{ marginBottom: 15 }}
            onChange={({ target }) => {
              const { checked: value } = target as HTMLInputElement
              if (value) {
                onChange((value ? [...bookableDesks] : [...requiredDesks]).sort())
              } else {
                setCheckboxValue({ id: 'all', value })
                setIsOpen(true)
              }
            }}
          >
            Select All
          </Checkbox>
        </Confirmation>
      ) : (
        <Checkbox
          checked={(bookableSpaceTypes || []).length === bookableDesks.length}
          style={{ marginBottom: 15 }}
          onChange={({ target }) => {
            const { checked: value } = target as HTMLInputElement
            onChange((value ? [...bookableDesks] : [...requiredDesks]).sort())
          }}
        >
          Select All
        </Checkbox>
      )}
      <div className="flex flex-wrap">
        {bookableDesks.map((e, i) => (
          <div key={i} style={{ width: '33%' }}>
            {hasConfirmBox ? (
              <Confirmation
                noIcon
                text={saveContent(e)}
                cancelable
                isOpen={isOpen && checkboxValue.id === e}
                onConfirm={() => {
                  const { id = '', value = false } = checkboxValue
                  const bookableSpaceDesks = value
                    ? [...bookableSpaceTypes, id]
                    : bookableSpaceTypes.filter((k) => k !== id)
                  onChange(bookableSpaceDesks.sort())
                  setIsOpen(false)
                }}
                onCancel={() => setIsOpen(false)}
                onClosing={() => {
                  setCheckboxValue({})
                }}
              >
                <Checkbox
                  id={e}
                  disabled={requiredDesks.includes(e)}
                  checked={requiredDesks.includes(e) || (bookableSpaceTypes || []).includes(e)}
                  label={e.split('_').map(capitalizeStr).join(' ')}
                  style={{ marginTop: 8 }}
                  onChange={({ target }) => {
                    const { id, checked: value } = target as HTMLInputElement
                    if (value) {
                      const bookableSpaceDesks = value
                        ? [...bookableSpaceTypes, id]
                        : bookableSpaceTypes.filter((k) => k !== id)
                      onChange(bookableSpaceDesks.sort())
                    } else {
                      setIsOpen(true)
                      setCheckboxValue({ id, value })
                    }
                  }}
                />
              </Confirmation>
            ) : (
              <Checkbox
                id={e}
                disabled={requiredDesks.includes(e)}
                checked={requiredDesks.includes(e) || (bookableSpaceTypes || []).includes(e)}
                label={e.split('_').map(capitalizeStr).join(' ')}
                style={{ marginTop: 8 }}
                onChange={({ target }) => {
                  const { id, checked: value } = target as HTMLInputElement
                  const bookableSpaceDesks = value
                    ? [...bookableSpaceTypes, id]
                    : bookableSpaceTypes.filter((k) => k !== id)
                  onChange(bookableSpaceDesks.sort())
                }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
